import type { GroupBase, OptionsOrGroups } from 'react-select';

import { isOfType } from '@enterprise/core';

/**
 * Calculates the total number of options, including group labels, in a given options or groups array.
 *
 * If the input is an array of groups, it sums the number of options in each group and adds one for each group label.
 * If the input is a flat array of options, it simply returns the length of the array.
 *
 * @typeParam Option - The type of the option objects.
 * @typeParam Group - The type of the group objects, extending GroupBase<Option>.
 * @param options - The array of options or groups to calculate the length for. Can be undefined.
 * @returns The total count of options, including group labels if present.
 */
export const calculateLength = <Option, Group extends GroupBase<Option>>(
  options: OptionsOrGroups<Option, Group> | undefined
) => {
  const calculated = options ?? [];
  const first = calculated[0] ?? {};
  const isGroup = isOfType<GroupBase<Option>>(first, 'options');

  if (isGroup) {
    return calculated.reduce((previous, current: Group | Option) => {
      return previous + (current as Group).options.length + 1;
    }, 0);
  }

  return calculated.length;
};
